import * as React from 'react';
import { useCallback, useState } from 'react';

import { BACKSPACE_KEY, ENTER_KEY, ESC_KEY, LEGACY_SPACE_KEY, SPACE_KEY } from '../../../../constants';
import { useSuggestEditStage, useTagInputPaste } from '../../../../hooks/tagCloudTargeting';
import { Bem } from '../../../../service/Bem/Bem';
import {
    findDictionaryEquivalent,
    onVersionedTagEditEnd
} from '../../../../service/TagCloudTargetingHelper/TagCloudTargetingHelper';
import { DictionaryItem } from '../../../../typings/common';
import { TagEditorProps } from '../../../../typings/tagCloudTargeting';
import { TagSuggestDecorator } from '../../TagSuggestDecorator/TagSuggestDecorator';

import './TagSuggestWithVersionEditor.css';

const BLOCK = 'tag-suggest-with-version-editor';

export interface TagSuggestWithVersionEditorProps extends TagEditorProps {
    suggestItems: DictionaryItem[];
}

export const TagSuggestWithVersionEditor: React.FC<TagSuggestWithVersionEditorProps> = React.memo(props => {
    const { tagValue, suggestItems, onCancel, onRefChange, onFocus, index } = props;
    const internalValue = Array.isArray(tagValue) ? tagValue : [];
    const [cachedValue, setCachedValue] = useState<string[]>(internalValue);
    const [inputValue, setInputValue] = useState('');
    const { editStage, setEditStage, items } = useSuggestEditStage(suggestItems, cachedValue);

    const dropValue = useCallback(() => {
        setEditStage(0);
        setInputValue('');
        setCachedValue([]);
    }, [setEditStage, setInputValue, setCachedValue]);

    const onSelect = useCallback(
        (item: DictionaryItem) => {
            setCachedValue([...cachedValue, item.text]);
            setInputValue('');
            setEditStage(editStage + 1);
        },
        [cachedValue, editStage]
    );

    const onChange = useCallback(
        (value: string) => {
            setInputValue(value);
        },
        [setInputValue]
    );

    const onKeyDown = useCallback(
        (evt: React.KeyboardEvent<HTMLInputElement>) => {
            switch (evt.key) {
                case ENTER_KEY:
                    evt.preventDefault();
                    onVersionedTagEditEnd(cachedValue, inputValue, props, dropValue);
                    break;

                case SPACE_KEY:
                case LEGACY_SPACE_KEY:
                    evt.preventDefault();

                    if (editStage === 0 || editStage === 1 || editStage === 3) {
                        const dictionaryValue = findDictionaryEquivalent(inputValue, suggestItems);
                        if (dictionaryValue) {
                            setCachedValue([...cachedValue, dictionaryValue]);
                            setInputValue('');
                            setEditStage(editStage + 1);
                        }
                    } else if (inputValue.trim()) {
                        setCachedValue([...cachedValue, inputValue.trim()]);
                        setInputValue('');
                        setEditStage(editStage + 1);
                    }
                    return;

                case BACKSPACE_KEY:
                    if (inputValue.length === 0) {
                        setEditStage(editStage === 0 ? 0 : editStage - 1);
                        const newCachedValue = [...cachedValue];
                        setInputValue(newCachedValue.pop() || '');
                        setCachedValue(newCachedValue);
                    }
                    return;

                case ESC_KEY:
                    onCancel();
                    dropValue();
                    return;

                default:
                    return;
            }
        },
        [cachedValue, inputValue, editStage]
    );

    const onBlur = useCallback(() => {
        onVersionedTagEditEnd(cachedValue, inputValue, props, dropValue, true);
    }, [cachedValue, inputValue, index]);

    return (
        <div className={Bem.cls(BLOCK)}>
            {cachedValue && <div className={Bem.cls(BLOCK, 'value')}>{cachedValue.join(' ')}</div>}
            <TagSuggestDecorator
                text={inputValue}
                suggestItems={items}
                onBlur={onBlur}
                onFocus={onFocus}
                onChange={onChange}
                onKeyDown={onKeyDown}
                onRefChange={onRefChange}
                onSelect={onSelect}
                onPaste={useTagInputPaste(props)}
            />
        </div>
    );
});
